import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';

interface Reminder {
  id: string;
  title: string;
  description?: string;
  remind_at: string;
  repeat?: 'none' | 'daily' | 'weekly' | 'monthly';
  is_completed: boolean;
}

export default function RemindersPage() {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('active');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    remind_at: '',
    repeat: 'none' as 'none' | 'daily' | 'weekly' | 'monthly',
  });

  useEffect(() => {
    loadReminders();
  }, []);

  const loadReminders = async () => {
    setIsLoading(true);
    const result = await apiClient.getNotes({ category: 'reminder' });
    if (result.data) {
      const reminderData = result.data.notes.map((note: any) => ({
        id: note.id,
        title: note.title,
        description: note.content,
        remind_at: note.metadata?.remind_at || note.created_at,
        repeat: note.metadata?.repeat || 'none',
        is_completed: note.metadata?.is_completed || false,
      }));
      setReminders(reminderData);
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const noteData = {
      title: formData.title,
      content: formData.description,
      category: 'reminder',
      metadata: {
        remind_at: formData.remind_at,
        repeat: formData.repeat,
        is_completed: false,
      },
    };

    const result = await apiClient.createNote(noteData);
    if (result.data) {
      loadReminders();
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        remind_at: '',
        repeat: 'none',
      });
    }
  };

  const handleToggleComplete = async (id: string, isCompleted: boolean) => {
    const reminder = reminders.find(r => r.id === id);
    if (!reminder) return;

    const result = await apiClient.updateNote(id, {
      metadata: {
        ...reminder,
        is_completed: !isCompleted,
      },
    });

    if (result.data) {
      setReminders(reminders.map(r =>
        r.id === id ? { ...r, is_completed: !isCompleted } : r
      ));
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除这个提醒吗？')) {
      const result = await apiClient.deleteNote(id);
      if (result.data) {
        setReminders(reminders.filter(r => r.id !== id));
      }
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (diff < 0) return '已过期';
    if (days > 7) return date.toLocaleDateString('zh-CN');
    if (days > 0) return `${days}天后`;
    if (hours > 0) return `${hours}小时后`;
    return '即将到期';
  };

  const getRepeatText = (repeat: string) => {
    switch (repeat) {
      case 'daily': return '每天';
      case 'weekly': return '每周';
      case 'monthly': return '每月';
      default: return '不重复';
    }
  };

  const filteredReminders = reminders.filter(r => {
    if (filter === 'active') return !r.is_completed;
    if (filter === 'completed') return r.is_completed;
    return true;
  });

  const sortedReminders = [...filteredReminders].sort((a, b) => {
    if (a.is_completed !== b.is_completed) {
      return a.is_completed ? 1 : -1;
    }
    return new Date(a.remind_at).getTime() - new Date(b.remind_at).getTime();
  });

  return (
    <div className="reminders-page">
      <div className="reminders-header">
        <h2>⏰ 提醒</h2>
        <button onClick={() => setShowForm(!showForm)} className="add-btn">
          {showForm ? '取消' : '+ 添加提醒'}
        </button>
      </div>

      <div className="filter-tabs">
        <button
          className={filter === 'active' ? 'active' : ''}
          onClick={() => setFilter('active')}
        >
          待办 ({reminders.filter(r => !r.is_completed).length})
        </button>
        <button
          className={filter === 'completed' ? 'active' : ''}
          onClick={() => setFilter('completed')}
        >
          已完成 ({reminders.filter(r => r.is_completed).length})
        </button>
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          全部 ({reminders.length})
        </button>
      </div>

      {showForm && (
        <div className="reminder-form">
          <h3>添加提醒</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>标题 *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="例如：买菜、开会"
                required
              />
            </div>

            <div className="form-group">
              <label>提醒时间 *</label>
              <input
                type="datetime-local"
                value={formData.remind_at}
                onChange={(e) => setFormData({ ...formData, remind_at: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>重复</label>
              <select
                value={formData.repeat}
                onChange={(e) => setFormData({ ...formData, repeat: e.target.value as any })}
              >
                <option value="none">不重复</option>
                <option value="daily">每天</option>
                <option value="weekly">每周</option>
                <option value="monthly">每月</option>
              </select>
            </div>

            <div className="form-group">
              <label>描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="提醒详情"
                rows={3}
              />
            </div>

            <button type="submit" className="submit-btn">保存</button>
          </form>
        </div>
      )}

      <div className="reminders-list">
        {isLoading ? (
          <div className="loading">加载中...</div>
        ) : sortedReminders.length === 0 ? (
          <div className="empty">
            <p>{filter === 'completed' ? '暂无已完成的提醒' : '暂无提醒'}</p>
            {filter !== 'completed' && (
              <button onClick={() => setShowForm(true)}>添加第一个提醒</button>
            )}
          </div>
        ) : (
          sortedReminders.map(reminder => (
            <div
              key={reminder.id}
              className={`reminder-item ${reminder.is_completed ? 'completed' : ''}`}
            >
              <input
                type="checkbox"
                checked={reminder.is_completed}
                onChange={() => handleToggleComplete(reminder.id, reminder.is_completed)}
                className="reminder-checkbox"
              />
              <div className="reminder-content">
                <div className="reminder-title">{reminder.title}</div>
                {reminder.description && (
                  <div className="reminder-description">{reminder.description}</div>
                )}
                <div className="reminder-meta">
                  <span className="reminder-time">
                    {formatDateTime(reminder.remind_at)}
                  </span>
                  {reminder.repeat && reminder.repeat !== 'none' && (
                    <span className="reminder-repeat">
                      🔁 {getRepeatText(reminder.repeat)}
                    </span>
                  )}
                </div>
              </div>
              <button
                className="delete-btn"
                onClick={() => handleDelete(reminder.id)}
                title="删除"
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>

      <style>{`
        .reminders-page {
          padding: 24px;
          max-width: 800px;
          margin: 0 auto;
        }

        .reminders-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .reminders-header h2 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          color: #333;
        }

        .add-btn {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .add-btn:hover {
          background: #0056b3;
        }

        .filter-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          background: white;
          padding: 8px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .filter-tabs button {
          flex: 1;
          padding: 8px 16px;
          border: none;
          background: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-tabs button:hover {
          background: #f5f5f5;
        }

        .filter-tabs button.active {
          background: #007bff;
          color: white;
        }

        .reminder-form {
          background: white;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
        }

        .reminder-form h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          font-weight: 600;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          border-color: #007bff;
        }

        .submit-btn {
          width: 100%;
          padding: 12px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .submit-btn:hover {
          background: #0056b3;
        }

        .reminders-list {
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .reminder-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.2s;
        }

        .reminder-item:hover {
          background: #f9f9f9;
        }

        .reminder-item:last-child {
          border-bottom: none;
        }

        .reminder-item.completed {
          opacity: 0.6;
        }

        .reminder-item.completed .reminder-title {
          text-decoration: line-through;
        }

        .reminder-checkbox {
          width: 20px;
          height: 20px;
          margin-top: 2px;
          cursor: pointer;
          flex-shrink: 0;
        }

        .reminder-content {
          flex: 1;
        }

        .reminder-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 4px;
          color: #333;
        }

        .reminder-description {
          font-size: 13px;
          color: #666;
          margin-bottom: 6px;
        }

        .reminder-meta {
          display: flex;
          gap: 12px;
          font-size: 12px;
          color: #999;
        }

        .reminder-time {
          display: flex;
          align-items: center;
        }

        .reminder-repeat {
          display: flex;
          align-items: center;
        }

        .delete-btn {
          background: none;
          border: none;
          font-size: 24px;
          color: #999;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .delete-btn:hover {
          background: #ffebee;
          color: #f44336;
        }

        .loading,
        .empty {
          text-align: center;
          padding: 60px 20px;
          color: #999;
        }

        .empty p {
          font-size: 16px;
          margin-bottom: 16px;
        }

        .empty button {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .empty button:hover {
          background: #0056b3;
        }

        @media (max-width: 768px) {
          .reminders-page {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  );
}
